import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PanAppState, UsfTokenStorageService } from '@panamax/app-state';
import { UserService } from '@app/user/services';
import { LoginService } from '@app/login/services/login.service';
import { filter, take, tap, switchMap, map, catchError } from 'rxjs/operators';
import { FEATURES } from '@shared/constants/splitio-features';
import { UserDataService } from '@app/user/services/user-data.service';
import { combineLatestWith, Observable, of } from 'rxjs';
import { UserState } from '@panamax/app-state';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  constructor(
    private http: HttpClient,
    private panAppState: PanAppState,
    private userService: UserService,
    private loginService: LoginService,
    private userDataService: UserDataService,
    private usfTokenStorageService: UsfTokenStorageService,
  ) {}

  initializeLocationService(): void {
    const confirmedZip = this.usfTokenStorageService.getGuestZip();
    const userState$ = this.panAppState.user$;
    const isGuestUser$ = this.userService.isGuestUser$();
    const isFeatureEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_guest_geo.name,
    ]);

    isGuestUser$
      .pipe(
        combineLatestWith(isFeatureEnabled$, confirmedZip, userState$),
        filter(([isGuestUser, isFeatureEnabled, _confirmedZip, userState]) => {
          return (
            (userState as UserState)?.userId === 'GUESTUSER' &&
            isFeatureEnabled &&
            !!isGuestUser
          );
        }),
        tap(() => console.log('[LocationService] Initializing')),
        filter(
          ([_isGuestUser, _isFeatureEnabled, confirmedZip, _userState]) => {
            // If valid confirmed zip exists, don't proceed with geolocation
            if (
              !!confirmedZip &&
              this.validateZipCode(confirmedZip.toString())
            ) {
              console.log(
                '[LocationService] Skipping geolocation - confirmed zip exists',
                confirmedZip,
              );
              return false;
            }
            return true;
          },
        ),
        take(1),
        switchMap(() => this.getGeolocation()), //Request location permissions if necessary and get the Lat/Long
        switchMap(position => {
          if (!position) {
            return of(null);
          }

          return this.userDataService
            .getZipCodeByLatLong(
              position.coords.latitude,
              position.coords.longitude,
            )
            .pipe(
              take(1),
              map(response => response?.postalCode),
            );
        }),
        tap(zipCode => {
          if (zipCode) {
            console.log('[LocationService] Zip code detected:', zipCode);
            this.handleZipCodeResponse(zipCode);
          } else {
            console.log(
              '[LocationService] No zip code detected for given coordinates',
            );
            this.saveGuestZip(null);
          }
        }),
        catchError(error => {
          console.error('[LocationService] Error:', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  private getGeolocation(): Observable<GeolocationPosition> {
    console.log('[LocationService] Requesting guest location');

    return new Observable<GeolocationPosition>(observer => {
      if (
        Capacitor.isNativePlatform() &&
        Capacitor.isPluginAvailable('Geolocation')
      ) {
        // Use Capacitor Geolocation plugin on device
        Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          maximumAge: 30000,
          timeout: 10000,
        })
          .then(position => {
            // Convert Capacitor position to GeolocationPosition-like object
            const coords = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
            observer.next({
              coords,
              timestamp: position.timestamp,
            } as GeolocationPosition);
            observer.complete();
          })
          .catch(error => {
            console.warn('[LocationService] Capacitor: Location error.', error);
            this.saveGuestZip(null);
            observer.error(error);
          });
      } else if (!Capacitor.isNativePlatform() && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position: GeolocationPosition) => {
            console.log(
              '[LocationService] Position data received - Lat/Long:',
              position.coords.latitude,
              position.coords.longitude,
            );
            observer.next(position);
            observer.complete();
          },
          error => {
            console.error('[LocationService] Location error:', error);
            this.saveGuestZip(null);
            observer.error(error);
          },
          {
            enableHighAccuracy: false,
            maximumAge: 30000,
            timeout: 10000,
          },
        );
      } else {
        observer.error(new Error('Geolocation not available'));
      }
    });
  }

  private handleZipCodeResponse(zipCode: string): void {
    if (this.validateZipCode(zipCode)) {
      this.loginService.switchGuestZip(parseInt(zipCode, 10), true);
    }
  }

  saveGuestZip(zipCode: number) {
    this.usfTokenStorageService.saveGuestZip(zipCode);
  }

  private validateZipCode(zipCode: string): boolean {
    if (!zipCode) {
      return false;
    }

    const paddedZipCode = zipCode.padStart(5, '0'); // Zip codes with leading zeros have less than 5 digits after numeric conversion, so we add those 0's back before validation
    const zipCodePattern = /^\d{5}$/; //Validate that the zipCode is valid format (5-digit numeric)
    return zipCodePattern.test(paddedZipCode);
  }
}
